import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { AlertTriangle, Info, CheckCircle2, Bell } from "lucide-react";
import api from "../lib/api";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../lib/utils";

const kindIcon = { critical: AlertTriangle, warning: AlertTriangle, info: Info, success: CheckCircle2 };
const kindColor = {
  critical: "text-red-600 bg-red-100 dark:bg-red-950/40",
  warning: "text-amber-600 bg-amber-100 dark:bg-amber-950/40",
  info: "text-blue-600 bg-blue-100 dark:bg-blue-950/40",
  success: "text-green-600 bg-green-100 dark:bg-green-950/40",
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-1">
            System alerts, mentions, and workflow events.
          </p>
        </div>
        <Button variant="outline" data-testid="notifications-mark-all" onClick={markAll}>
          Mark all as read
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Inbox</CardTitle>
          <CardDescription>Ordered by most recent.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <div className="py-8 text-center text-muted-foreground">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="py-12 text-center text-muted-foreground flex flex-col items-center gap-2">
              <Bell className="w-8 h-8" />
              You&apos;re all caught up.
            </div>
          )}
          <ul className="divide-y divide-border">
            {items.map((n) => {
              const Icon = kindIcon[n.kind] || Info;
              return (
                <li
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  className={cn(
                    "px-6 py-4 flex gap-3 transition-colors",
                    !n.read && "bg-primary-subtle/40 dark:bg-primary/10"
                  )}
                >
                  <div className={cn("w-9 h-9 rounded-md flex items-center justify-center shrink-0", kindColor[n.kind] || kindColor.info)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-sm text-muted-foreground">{n.message}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </div>
                  </div>
                  {!n.read && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`notification-mark-${n.id}`}
                      onClick={() => markOne(n.id)}
                    >
                      Mark read
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
